// src/common/hooks/useRootLayout.ts
import { useCallback, useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { setStoreList, setForceStores } from "../store/slices/storeListSlice";
import { setCategoryList } from "../store/slices/categoryListSlice";
import { setCurrentCluster } from "../store/slices/currentClusterSlice";
import { store } from "../store/store";
import { setOrders } from "../store/slices/orderSlice";
import { setPaymentMethods } from "../store/slices/paymentMethodsSlice";
import { setComplexOrders } from "../store/slices/complexOrderListSlice";
import {
  setComplexOrderCartItems,
  setComplexCartPhone,
  setComplexCartComment,
  setComplexCartFieldValues,
  ComplexOrderCartState,
} from "../store/slices/complexOrderCartSlice";

import {
  SaleListResponse,
  StoreListResponse,
  CategoryListResponse,
  ClusterInterface,
  OrderInterface,
  PromoInterface,
  FullAddress,
  CartItem,
  BonusTransaction,
  PaymentMethodInterface,
  ComplexOrderInterface,
} from "../common/types/types";
import { setAddresses } from "../store/slices/addressSlice";
import { setPromos, selectPromos } from "../store/slices/promoSlice";
import { setCart } from "../store/slices/cartSlice";
import { setBonusData, setBonusHistory } from "../store/slices/bonusSlice";
import useInterval from "@use-it/interval";
import { addPendingAction } from "../store/slices/syncSlice";
import {
  setSaleItemList,
  upsertItems,
} from "../store/slices/saleItemListSlice";
import {
  getFromStorage,
  saveToStorage,
} from "../common/utils/functions/localStorage";
import { getApi, initAppAuth } from "../api/api";
import { useCommonLoading } from "../common/hooks/useCommonLoading";
import { useSyncPendingActions } from "../common/hooks/useSyncPendingActions";
import { getCacheVersion } from "../config/runtimeConfig";
import { useAutoLogin } from "../common/hooks/useAutoLogin";
import { setFavorites } from "../store/slices/favoritesSlice";
import { selectCurrentCluster } from "../store/slices/currentClusterSlice";
import { setExecutor } from "../store/slices/executorSlice";
import { useInitGMap } from "../common/hooks/useInitGMap";
import { useOrdersPolling } from "../common/hooks/useOrdersPolling";

const getCacheKeys = () => {
  const version = getCacheVersion();
  return {
    SALE_ITEMS: `saleItemList_v${version}`,
    STORES: `storeList_v${version}`,
    CATEGORIES: `categoryList_v${version}`,
    CLUSTER: `cluster_v${version}`,
    ORDERS: `orders_v${version}`,
    COMPLEX_ORDERS: `complexOrders_v${version}`,
    COMPLEX_CART: `complexOrderCart_v${version}`,
    PROMOS: `promos_v${version}`,
    ADDRESSES: `addresses_v${version}`,
    CART: `cart_v${version}`,
    SYNC_PENDING: `sync_pending_v${version}`,
    BONUS_BALANCE: `bonus_balance_v${version}`,
    BONUS_HISTORY: `bonus_history_v${version}`,
    PAYMENT_METHODS: `payment_methods_v${version}`,
    FORCE_STORES: `forceStores_v${version}`,
    FAVORITES: `favorites_v${version}`,
  };
};

export const globalSync = {
  syncNow: null as (() => Promise<void>) | null,
  refreshCatalog: null as (() => Promise<void>) | null,
};

export const useRootLayout = () => {
  const { setCommonLoading } = useCommonLoading();
  const currentUser = store.getState().currentUser;
  const currentCluster = useSelector(selectCurrentCluster);
  const [hasNetwork, setHasNetwork] = useState<boolean | null>(null);
  const [hasDataError, setHasDataError] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const [shouldAutoCheck, setShouldAutoCheck] = useState(false);

  useAutoLogin();
  useInitGMap();
  useOrdersPolling();

  const stateRef = useRef({
    hasNetwork,
    shouldAutoCheck,
    isMounted: true,
  });

  const { syncNow } = useSyncPendingActions();

  const saveToCache = useCallback(async <T>(key: string, data: T) => {
    try {
      saveToStorage(key, data);
    } catch (error) {
      console.error(`Ошибка сохранения кэша ${key}:`, error);
    }
  }, []);

  const loadFromCache = useCallback(
    async <T>(key: string): Promise<T | null> => {
      try {
        return getFromStorage<T>(key);
      } catch (error) {
        console.error(`Ошибка загрузки кэша ${key}:`, error);
        return null;
      }
    },
    [],
  );

  const checkNetwork = useCallback(async (): Promise<boolean> => {
    try {
      const api = getApi();
      await api.healthCheck(1000);
      return true;
    } catch (error: any) {
      if (error.response) return true;
      return false;
    }
  }, []);

  const checkIfAnyCacheExists = useCallback(async (): Promise<boolean> => {
    const CACHE_KEYS = getCacheKeys();
    const results = await Promise.all([
      loadFromCache(CACHE_KEYS.CLUSTER),
      loadFromCache(CACHE_KEYS.CATEGORIES),
      loadFromCache(CACHE_KEYS.SALE_ITEMS),
    ]);
    return results.some((result) => result !== null);
  }, [loadFromCache]);

  const loadAllFromCache = useCallback(async () => {
    const CACHE_KEYS = getCacheKeys();

    const cluster = await loadFromCache<ClusterInterface>(CACHE_KEYS.CLUSTER);
    if (cluster) store.dispatch(setCurrentCluster(cluster));

    const categories = await loadFromCache<CategoryListResponse>(
      CACHE_KEYS.CATEGORIES,
    );
    if (categories?.data) store.dispatch(setCategoryList(categories));

    const promos = await loadFromCache<PromoInterface[]>(CACHE_KEYS.PROMOS);
    if (promos) store.dispatch(setPromos(promos));

    const saleItems = await loadFromCache<SaleListResponse>(
      CACHE_KEYS.SALE_ITEMS,
    );
    if (saleItems) store.dispatch(setSaleItemList(saleItems));

    const stores = await loadFromCache<StoreListResponse>(CACHE_KEYS.STORES);
    if (stores) store.dispatch(setStoreList(stores));

    const addresses = await loadFromCache<FullAddress[]>(CACHE_KEYS.ADDRESSES);
    if (addresses) store.dispatch(setAddresses(addresses));

    const cart = await loadFromCache<CartItem[]>(CACHE_KEYS.CART);
    if (cart && Array.isArray(cart)) {
      store.dispatch(setCart(cart));
    }

    const complexCart = await loadFromCache<ComplexOrderCartState>(
      CACHE_KEYS.COMPLEX_CART,
    );
    if (complexCart) {
      if (complexCart.items && Array.isArray(complexCart.items)) {
        store.dispatch(setComplexOrderCartItems(complexCart.items));
      }
      if (complexCart.phone) {
        store.dispatch(setComplexCartPhone(complexCart.phone));
      }
      if (complexCart.comment) {
        store.dispatch(setComplexCartComment(complexCart.comment));
      }
      if (complexCart.fieldValues && Array.isArray(complexCart.fieldValues)) {
        store.dispatch(setComplexCartFieldValues(complexCart.fieldValues));
      }
    }

    const forceStores = await loadFromCache<number[]>(CACHE_KEYS.FORCE_STORES);
    if (forceStores && Array.isArray(forceStores)) {
      store.dispatch(setForceStores(forceStores));
    }

    const bonusBalance = await loadFromCache<{
      balance: number;
      totalSpent: number;
      bonusPercent: number;
      level?: string;
    }>(CACHE_KEYS.BONUS_BALANCE);
    if (bonusBalance) {
      store.dispatch(setBonusData(bonusBalance));
    }

    const bonusHistory = await loadFromCache<BonusTransaction[]>(
      CACHE_KEYS.BONUS_HISTORY,
    );
    if (bonusHistory) {
      store.dispatch(setBonusHistory(bonusHistory));
    }

    const paymentMethods = await loadFromCache<PaymentMethodInterface[]>(
      CACHE_KEYS.PAYMENT_METHODS,
    );
    if (paymentMethods) {
      store.dispatch(setPaymentMethods(paymentMethods));
    }

    const favorites = await loadFromCache<SaleListResponse>(
      CACHE_KEYS.FAVORITES,
    );
    if (favorites?.data) {
      store.dispatch(setFavorites(favorites));
    }

    const syncPending = await loadFromCache<{
      actions: any[];
      timestamp: number;
    }>(CACHE_KEYS.SYNC_PENDING);
    if (syncPending?.actions?.length) {
      syncPending.actions.forEach((action) => {
        store.dispatch(addPendingAction(action));
      });
    }
  }, [loadFromCache]);

  const loadGiftAndOrderItems = useCallback(
    async (orders: OrderInterface[], promosData: PromoInterface[]) => {
      const allIds: number[] = [];

      // 1. ID товаров из заказов
      const orderItemIds = orders.flatMap(
        (order) => order.items?.map((item) => Number(item.itemId)) || [],
      );
      allIds.push(...orderItemIds);

      // 2. ID товаров-подарков из акций
      const giftPromos = promosData.filter(
        (p) => p.giftBySum && p.giftItemIds?.length,
      );
      const giftItemIds = giftPromos.flatMap((p) => p.giftItemIds || []);
      allIds.push(...giftItemIds);

      // 3. Уникальные ID
      const uniqueIds = [...new Set(allIds)];

      // 4. Загружаем недостающие товары
      if (uniqueIds.length) {
        try {
          const api = getApi();
          const items = await api.getSaleItemListByIds({ ids: uniqueIds });
          if (items?.data?.length) {
            store.dispatch(upsertItems(items.data));
          }
        } catch (error) {
          console.error("Ошибка загрузки товаров по ID:", error);
        }
      }
    },
    [],
  );

  const loadAllFromNetwork = useCallback(async () => {
    const api = getApi();
    const CACHE_KEYS = getCacheKeys();

    try {
      const [cluster, categories, promos, saleItems] = await Promise.allSettled(
        [
          api.getCluster().catch(() => null),
          api.getCategoryList().catch(() => null),
          api.getPromoList().catch(() => ({ data: [] })),
          api.getSaleItemList({ limit: 50, offset: 0 }).catch(() => null),
        ],
      );

      if (cluster.status === "fulfilled" && cluster.value) {
        store.dispatch(setCurrentCluster(cluster.value));
        saveToCache(CACHE_KEYS.CLUSTER, cluster.value);
      }

      if (categories.status === "fulfilled" && categories.value) {
        store.dispatch(setCategoryList(categories.value));
        saveToCache(CACHE_KEYS.CATEGORIES, categories.value);
      }

      let promosData: PromoInterface[] = [];
      if (promos.status === "fulfilled" && promos.value?.data) {
        promosData = promos.value.data;
        store.dispatch(setPromos(promosData));
        saveToCache(CACHE_KEYS.PROMOS, promosData);
      }

      if (saleItems.status === "fulfilled" && saleItems.value) {
        store.dispatch(setSaleItemList(saleItems.value));
        saveToCache(CACHE_KEYS.SALE_ITEMS, saleItems.value);
      }

      const [stores, addresses] = await Promise.allSettled([
        api.getStoreList().catch(() => null),
        api.getAddressList().catch(() => []),
      ]);

      if (stores.status === "fulfilled" && stores.value) {
        store.dispatch(setStoreList(stores.value));
        saveToCache(CACHE_KEYS.STORES, stores.value);
      }

      if (addresses.status === "fulfilled" && addresses.value) {
        store.dispatch(setAddresses(addresses.value));
        saveToCache(CACHE_KEYS.ADDRESSES, addresses.value);
      }

      // Загружаем заказы ТОЛЬКО если есть пользователь
      if (currentUser) {
        try {
          const result = await api.getOrderList();
          const orders = Array.isArray(result) ? result : [];
          store.dispatch(setOrders(orders));
          saveToCache(CACHE_KEYS.ORDERS, orders);

          // Догружаем товары из заказов + подарки
          await loadGiftAndOrderItems(orders, promosData);
        } catch (error) {
          console.error("Ошибка загрузки заказов:", error);
        }
      }
    } catch (error) {
      console.error("Ошибка сетевой загрузки:", error);
    }
  }, [saveToCache, currentUser, loadGiftAndOrderItems]);

  const refreshCatalog = useCallback(async () => {
    const hasNet = await checkNetwork();
    if (!hasNet) {
      console.log("Нет сети, обновление каталога отменено");
      return;
    }

    try {
      await loadAllFromNetwork();
    } catch (error) {
      console.error("❌ Ошибка обновления каталога:", error);
    }
  }, [checkNetwork, loadAllFromNetwork]);

  useEffect(() => {
    globalSync.syncNow = syncNow;
    globalSync.refreshCatalog = refreshCatalog;
    return () => {
      globalSync.syncNow = null;
      globalSync.refreshCatalog = null;
    };
  }, [syncNow, refreshCatalog]);

  useEffect(() => {
    stateRef.current.hasNetwork = hasNetwork;
    stateRef.current.shouldAutoCheck = shouldAutoCheck;
  }, [hasNetwork, shouldAutoCheck]);

  const reloadData = useCallback(() => {
    setReloadTrigger((prev) => prev + 1);
  }, []);

  useEffect(() => {
    stateRef.current.isMounted = true;

    const loadData = async () => {
      setCommonLoading(true);
      setHasDataError(false);

      try {
        await initAppAuth();
        await loadAllFromCache();
        setIsDataLoaded(true);

        const networkStatus = await checkNetwork();
        setHasNetwork(networkStatus);

        if (!networkStatus) {
          setShouldAutoCheck(true);
        }

        if (networkStatus) {
          await loadAllFromNetwork();
        } else {
          if (!(await checkIfAnyCacheExists())) {
            setHasDataError(true);
          }
        }
      } catch (error) {
        console.error("[RootLayout] Ошибка:", error);
        if (!(await checkIfAnyCacheExists())) {
          setHasDataError(true);
        }
      } finally {
        setCommonLoading(false);
      }
    };

    loadData();

    return () => {
      stateRef.current.isMounted = false;
    };
  }, [reloadTrigger]);

  useInterval(
    async () => {
      if (!stateRef.current.shouldAutoCheck || !stateRef.current.isMounted)
        return;

      try {
        const networkStatus = await checkNetwork();
        if (networkStatus && stateRef.current.isMounted) {
          setShouldAutoCheck(false);
          setHasNetwork(true);
          await loadAllFromNetwork();
        }
      } catch (error) {}
    },
    shouldAutoCheck ? 1000 : null,
  );

  useEffect(() => {
    const loadUserData = async () => {
      if (!currentUser || !isDataLoaded) return;

      try {
        const api = getApi();
        const CACHE_KEYS = getCacheKeys();

        // Проверка исполнителя
        if (hasNetwork) {
          try {
            const executorMe = await api.getExecutorMe();
            if (executorMe) {
              store.dispatch(setExecutor(executorMe));
            }
          } catch (error) {
            // не исполнитель
          }
        }

        // Загружаем заказы и догружаем товары
        if (hasNetwork) {
          try {
            const result = await api.getOrderList();
            const orders = Array.isArray(result) ? result : [];
            store.dispatch(setOrders(orders));
            saveToCache(CACHE_KEYS.ORDERS, orders);

            // Получаем акции из стора через селектор
            const state = store.getState();
            const promosData = selectPromos(state);

            // Догружаем товары из заказов + подарки
            const allIds: number[] = [];

            // ID товаров из заказов
            const orderItemIds = orders.flatMap(
              (order) => order.items?.map((item) => Number(item.itemId)) || [],
            );
            allIds.push(...orderItemIds);

            // ID товаров-подарков из акций
            const giftPromos = promosData.filter(
              (p: PromoInterface) => p.giftBySum && p.giftItemIds?.length,
            );
            const giftItemIds = giftPromos.flatMap(
              (p: PromoInterface) => p.giftItemIds || [],
            );
            allIds.push(...giftItemIds);

            const uniqueIds = [...new Set(allIds)];

            if (uniqueIds.length) {
              try {
                const items = await api.getSaleItemListByIds({
                  ids: uniqueIds,
                });
                if (items?.data?.length) {
                  store.dispatch(upsertItems(items.data));
                }
              } catch (error) {
                console.error("Ошибка загрузки товаров по ID:", error);
              }
            }
          } catch (error) {
            console.error("Ошибка загрузки заказов:", error);
          }

          // Бонусы
          try {
            const bonusData = await api.getBonusBalance();
            store.dispatch(setBonusData(bonusData));
            saveToCache(CACHE_KEYS.BONUS_BALANCE, bonusData);

            const historyResponse = await api.getBonusHistory(20, 0);
            if (historyResponse && Array.isArray(historyResponse)) {
              store.dispatch(setBonusHistory(historyResponse));
              saveToCache(CACHE_KEYS.BONUS_HISTORY, historyResponse);
            }
          } catch (error) {
            console.error("Ошибка загрузки бонусов:", error);
          }

          try {
            const paymentMethodsResponse = await api.getPaymentMethods();
            if (paymentMethodsResponse?.data) {
              store.dispatch(setPaymentMethods(paymentMethodsResponse.data));
              saveToCache(
                CACHE_KEYS.PAYMENT_METHODS,
                paymentMethodsResponse.data,
              );
            }
          } catch (error) {
            console.error("Ошибка загрузки способов оплаты:", error);
          }

          try {
            const favoritesResponse = await api.getUserFavorites({
              limit: 1000,
            });
            if (favoritesResponse) {
              store.dispatch(setFavorites(favoritesResponse));
              saveToCache(CACHE_KEYS.FAVORITES, favoritesResponse);
            }
          } catch (error) {
            console.error("Ошибка загрузки избранного:", error);
          }
        } else {
          // Офлайн: загружаем из кэша
          const cachedOrders = await loadFromCache<OrderInterface[]>(
            CACHE_KEYS.ORDERS,
          );
          if (cachedOrders) store.dispatch(setOrders(cachedOrders));

          const cachedBonus = await loadFromCache<{
            balance: number;
            totalSpent: number;
            bonusPercent: number;
            level?: string;
          }>(CACHE_KEYS.BONUS_BALANCE);
          if (cachedBonus) store.dispatch(setBonusData(cachedBonus));

          const cachedHistory = await loadFromCache<BonusTransaction[]>(
            CACHE_KEYS.BONUS_HISTORY,
          );
          if (cachedHistory) store.dispatch(setBonusHistory(cachedHistory));

          const cachedPaymentMethods = await loadFromCache<
            PaymentMethodInterface[]
          >(CACHE_KEYS.PAYMENT_METHODS);
          if (cachedPaymentMethods)
            store.dispatch(setPaymentMethods(cachedPaymentMethods));

          const cachedFavorites = await loadFromCache<SaleListResponse>(
            CACHE_KEYS.FAVORITES,
          );
          if (cachedFavorites?.data)
            store.dispatch(setFavorites(cachedFavorites));
        }

        if (currentCluster?.complexSales === true) {
          try {
            if (hasNetwork) {
              const complexResult = await api.getComplexOrderList();
              const complexOrders = Array.isArray(complexResult)
                ? complexResult
                : [];
              store.dispatch(setComplexOrders(complexOrders));
              saveToCache(CACHE_KEYS.COMPLEX_ORDERS, complexOrders);
            } else {
              const cached = await loadFromCache<ComplexOrderInterface[]>(
                CACHE_KEYS.COMPLEX_ORDERS,
              );
              if (cached) store.dispatch(setComplexOrders(cached));
            }
          } catch (error) {
            console.error("Ошибка загрузки комплексных заявок:", error);
          }
        }
      } catch (error) {
        console.error("Ошибка загрузки пользовательских данных:", error);
      }
    };

    loadUserData();
  }, [currentUser, hasNetwork, isDataLoaded, currentCluster]);

  return {
    hasDataError,
    hasNetwork,
    isDataLoaded,
    reloadData,
  };
};
